/**
 * 🛡️ G2GENTE - Notificações do Candidato (Web)
 */
import React, { useState, useEffect } from 'react';
import CandidateLayout from '../../components/CandidateLayout';
import { notificationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Bell,
  Loader2,
  Briefcase,
  MessageSquare,
  UserCheck,
  CheckCircle2,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function CandidateNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, lida: true } : n
      ));
    } catch (error) {
      toast.error('Erro ao marcar notificação');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, lida: true })));
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar notificações');
    }
  };

  const getIcon = (tipo) => {
    const icons = {
      NEW_JOB: Briefcase,
      SOLICITATION: UserCheck,
      APPLICATION_UPDATE: CheckCircle2,
      MESSAGE: MessageSquare
    };
    return icons[tipo] || Bell;
  };

  const unreadCount = notifications.filter(n => !n.lida).length;

  if (loading) {
    return (
      <CandidateLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </CandidateLayout>
    );
  }

  return (
    <CandidateLayout>
      <div className="space-y-6" data-testid="candidate-notifications">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Notificações</h1>
              <p className="text-slate-600">
                {unreadCount > 0 ? `${unreadCount} não lida(s)` : 'Todas lidas'}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllAsRead}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Marcar todas como lidas
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-900 mb-2">Nenhuma notificação</h3>
              <p className="text-sm text-slate-600">
                Você será notificado sobre novas vagas e atualizações
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => {
              const Icon = getIcon(notification.tipo);
              return (
                <Card 
                  key={notification.id} 
                  className={`border-slate-200 transition-colors ${
                    !notification.lida ? 'bg-cyan-50 border-cyan-200' : ''
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        !notification.lida ? 'bg-cyan-100' : 'bg-slate-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          !notification.lida ? 'text-cyan-600' : 'text-slate-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`font-medium ${
                            !notification.lida ? 'text-slate-900' : 'text-slate-700'
                          }`}>
                            {notification.mensagem}
                          </p>
                          {!notification.lida && (
                            <Badge className="bg-cyan-600 text-xs">Nova</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {new Date(notification.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      {!notification.lida && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </CandidateLayout>
  );
}
